import React, { useState } from "react";

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from "@mui/material";

import { CopyBlock } from "@shell/components/CopyBlock";
import { SelectCard } from "@shell/components/SelectCard";

import { useUpsertInfraConfig } from "../../hooks/useInfraConfig";
import type { CloudProviderUI, UpsertInfraConfigRequest } from "../../types";

export interface CloudConnectionModalProps {
  open: boolean;
  onClose: () => void;
  projectAlias: string;
  onSuccess: () => void;
}

const PROVIDERS: {
  value: CloudProviderUI;
  label: string;
  description: string;
  infraType: string;
}[] = [
  {
    value: "rusha_hosted",
    label: "Rusha Hosted",
    description:
      "Managed infrastructure on Rusha's cluster. No setup required.",
    infraType: "rusha_hosted",
  },
  {
    value: "aws",
    label: "AWS EKS",
    description: "Amazon Elastic Kubernetes Service",
    infraType: "aws_eks",
  },
  {
    value: "gcp",
    label: "GCP GKE",
    description: "Google Kubernetes Engine",
    infraType: "gcp_gke",
  },
  {
    value: "azure",
    label: "Azure AKS",
    description: "Azure Kubernetes Service",
    infraType: "azure_aks",
  },
];

const REGIONS: Record<string, string[]> = {
  rusha_hosted: ["eu-central-1", "us-east-1"],
  aws: ["us-east-1", "us-west-2", "eu-central-1", "ap-southeast-1"],
  gcp: ["us-central1", "europe-west3", "asia-southeast1"],
  azure: ["eastus", "westeurope", "southeastasia"],
};

const AWS_TRUST_POLICY = `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Principal": {
        "AWS": "arn:aws:iam::RUSHA_ACCOUNT_ID:role/rusha-deployer"
      },
      "Action": "sts:AssumeRoleWithWebIdentity",
      "Condition": {
        "StringEquals": {
          "sts:ExternalId": "RUSHA_EXTERNAL_ID"
        }
      }
    }
  ]
}`;

export function CloudConnectionModal({
  open,
  onClose,
  projectAlias,
  onSuccess,
}: CloudConnectionModalProps): React.ReactElement {
  const [selectedProvider, setSelectedProvider] =
    useState<CloudProviderUI | null>(null);
  const [region, setRegion] = useState("");
  const { upsert, isSubmitting, submitError } = useUpsertInfraConfig();

  const handleSubmit = async (provider?: CloudProviderUI) => {
    const resolved = provider ?? selectedProvider;
    if (!resolved) return;
    const providerConfig = PROVIDERS.find((p) => p.value === resolved);
    if (!providerConfig) return;

    const dto: UpsertInfraConfigRequest = {
      cloudProvider: resolved,
      infraType: providerConfig.infraType,
      region: region || undefined,
    };

    const result = await upsert(projectAlias, dto);
    if (result) {
      onSuccess();
      onClose();
    }
  };

  const handleSkip = () => {
    void handleSubmit("rusha_hosted");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Connect Cloud Provider</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {submitError && <Alert severity="error">{submitError}</Alert>}

          <Typography variant="body2" color="text.secondary">
            Choose your cloud platform. Rusha uses cross-account IAM trust — no
            long-lived credentials stored.
          </Typography>

          {/* Provider selector */}
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Cloud Provider
            </Typography>
            <Stack spacing={1}>
              {PROVIDERS.map((p) => (
                <SelectCard
                  key={p.value}
                  label={p.label}
                  description={p.description}
                  selected={selectedProvider === p.value}
                  onClick={() => {
                    setSelectedProvider(p.value);
                    setRegion("");
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Provider-specific fields */}
          {selectedProvider && (
            <Stack spacing={2.5} sx={{ animation: "fadeIn 200ms ease" }}>
              {/* Region */}
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Region
                </Typography>
                <Stack spacing={0.5}>
                  {(REGIONS[selectedProvider] ?? []).map((r) => (
                    <SelectCard
                      key={r}
                      label={r}
                      selected={region === r}
                      onClick={() => setRegion(r)}
                    />
                  ))}
                </Stack>
              </Box>

              {/* AWS: Trust policy copy block */}
              {selectedProvider === "aws" && (
                <CopyBlock
                  label="IAM Trust Policy"
                  content={AWS_TRUST_POLICY}
                />
              )}

              {/* Rusha hosted: just info */}
              {selectedProvider === "rusha_hosted" && (
                <Alert severity="info">
                  Rusha Hosted provides managed PostgreSQL (CNPG), Redis, MinIO,
                  and observability on Rusha&apos;s cluster. No additional setup
                  required.
                </Alert>
              )}
            </Stack>
          )}

          <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2.5 }}>
        <Link
          component="button"
          type="button"
          onClick={handleSkip}
          sx={{ fontSize: "0.875rem", cursor: "pointer" }}
        >
          Prefer Rusha-hosted? Skip →
        </Link>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || !selectedProvider}
          >
            {isSubmitting ? "Connecting…" : "Connect"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
